import { ENTITY_ROUTE_ORDER, entitySlug, SageEntityTypeName } from './sage-integration.model';

// The first-time walkthrough on the Sage console (Mockup 03) and on the first-sync wizard's hub.

export type SageTourStatus = 'skipped' | 'done';

export interface SageTourStep {
  slug: string;
  title: string;
  text: string;
  aivor?: string;
  sage?: string;
}

// Mockup copy, except where it isn't true of the integration: Sage name and code edits do come back
// into Aivor, Aivor syncs no purchases or balances, and Sage sales groups are handled by the push.
const TOUR_STEP_COPY: Record<SageEntityTypeName, Required<Omit<SageTourStep, 'slug'>>> = {
  SupplierCategory: {
    title: 'Groups for your suppliers',
    text: 'Labels such as "Direct Materials" or "Fixed OH" that sort your suppliers. They are matched first, because your suppliers use them.',
    aivor: 'Supplier Categories',
    sage: 'Suppliers, Categories',
  },
  Supplier: {
    title: 'The companies you buy from',
    text: 'Each supplier in Aivor is matched to the same company in Sage, so both systems mean the same supplier. If two names look alike, we ask you first.',
    aivor: 'Suppliers',
    sage: 'Suppliers',
  },
  ItemCategory: {
    title: 'Groups for your stock items',
    text: 'Categories such as "Raw Materials" or "Finished Goods". Items use them, so they are matched before the items.',
    aivor: 'Item Categories',
    sage: 'Items, Item categories',
  },
  Item: {
    title: 'Your raw materials, finished goods and services',
    text: 'Each item is matched to the same item in Sage, with its unit of measure. Sage files every item under a sales group. If your Sage company has more than one, Aivor asks you which to use.',
    aivor: 'Items',
    sage: 'Items',
  },
  ItemBundle: {
    title: 'Products made from other items',
    text: 'A bundle, such as a table sold with six chairs, is made from items that are already matched, so bundles come last. Choose which finished goods go to Sage as bundles under Fix matches, on Finished goods.',
    aivor: 'Finished goods (as bundles)',
    sage: 'Item bundles',
  },
};

/** One step per record-type card on the page, in matching order. */
export function tourSteps(entityTypes: number[]): SageTourStep[] {
  const present = new Set(entityTypes.map(entitySlug).filter((slug): slug is string => !!slug));
  const routes = present.size ? ENTITY_ROUTE_ORDER.filter((r) => present.has(r.slug)) : ENTITY_ROUTE_ORDER;
  return routes.map((r) => ({ slug: r.slug, ...TOUR_STEP_COPY[r.type] }));
}

const TAB_TOUR_STEPS: SageTourStep[] = [
  {
    slug: 'tab-matches',
    title: 'Fix a wrong match',
    text: "If a Sage record was linked to the wrong Aivor record, change it here, or add it to Aivor as new. Nothing is deleted. Under Finished goods, choose whether each one goes to Sage as a Bundle, which lists the items it's made from, or as one Standalone item. Standalone is used until you choose.",
  },
  {
    slug: 'tab-runs',
    title: 'A record of every sync',
    text: 'Each sync, automatic or started with Sync now, is listed here, newest first: what synced, which way it went, how many records went through, and whether it worked.',
  },
  {
    slug: 'tab-conflicts',
    title: 'When a record changed on both sides',
    text: "If a record is changed in both Aivor and Sage between syncs, Aivor's version is kept and replaces Sage's. Each one is listed here with both values, so you can see what Sage had.",
  },
  {
    slug: 'tab-groups',
    title: "Sage's sales and purchase groups",
    text: 'Sage files every item under a sales group. Choose the default here for items without their own, and press Refresh from Sage after adding a group in Sage.',
  },
];

/** The cards, then one step per tab, in tab order. */
export function consoleTourSteps(entityTypes: number[]): SageTourStep[] {
  return [...tourSteps(entityTypes), ...TAB_TOUR_STEPS];
}

/** The wizard's tour says what its hub rows say (Mockup 01), so the words are changed in one place. */
export function wizardTourSteps(steps: { slug: string; label: string; copy: { why: string } }[]): SageTourStep[] {
  return steps.map((s) => ({ slug: s.slug, title: s.label, text: s.copy.why }));
}

const TOUR_KEY_PREFIX = 'sageTour.';
const WIZARD_TOUR_KEY_PREFIX = 'sageWizardTour.';

/** Per company and per user, so one admin finishing the tour doesn't hide it from another. */
export function tourStorageKey(tenantId: unknown, userId: unknown, prefix = TOUR_KEY_PREFIX): string | null {
  const tenant = tenantId == null ? '' : String(tenantId).trim();
  const user = userId == null ? '' : String(userId).trim();
  return tenant && user ? `${prefix}${tenant}.${user}` : null;
}

/** Its own flag: seeing the wizard's tour doesn't stand in for the console's, which covers other cards. */
export function wizardTourStorageKey(tenantId: unknown, userId: unknown): string | null {
  return tourStorageKey(tenantId, userId, WIZARD_TOUR_KEY_PREFIX);
}

export function readTourStatus(key: string | null): SageTourStatus | null {
  if (!key) return null;
  try {
    const value = localStorage.getItem(key);
    return value === 'skipped' || value === 'done' ? value : null;
  } catch {
    return null;
  }
}

export function writeTourStatus(key: string | null, status: SageTourStatus): void {
  if (!key) return;
  // Skipping a replay started from Help doesn't undo having finished the tour.
  if (status === 'skipped' && readTourStatus(key) === 'done') return;
  try {
    localStorage.setItem(key, status);
  } catch {
    // Storage blocked: the tour starts again on the next visit.
  }
}

export const PHONE_LAYOUT_QUERY = '(max-width: 767.98px)';

export interface TourBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface TourPoint {
  left: number;
  top: number;
}

export function inflate(box: TourBox, by: number): TourBox {
  return { left: box.left - by, top: box.top - by, width: box.width + by * 2, height: box.height + by * 2 };
}

const PANEL_GAP = 16;
const EDGE = 14;

/**
 * Below the target if it fits, else above it, else beside it (right, then left); failing all of
 * those, against the bottom of the window. 'center' centres the panel on the target, 'end' lines
 * up their right edges.
 */
export function placePanel(
  target: TourBox,
  panel: { width: number; height: number },
  viewport: { width: number; height: number },
  align: 'center' | 'end'
): TourPoint {
  const preferred = align === 'center' ? target.left + target.width / 2 - panel.width / 2 : target.left + target.width - panel.width;
  const left = Math.max(EDGE, Math.min(preferred, viewport.width - panel.width - EDGE));

  const below = target.top + target.height + PANEL_GAP;
  if (below + panel.height <= viewport.height - EDGE) return { left, top: below };
  const above = target.top - PANEL_GAP - panel.height;
  if (above >= EDGE) return { left, top: above };

  const sideTop = Math.max(EDGE, Math.min(target.top, viewport.height - panel.height - EDGE));
  const right = target.left + target.width + PANEL_GAP;
  if (right + panel.width <= viewport.width - EDGE) return { left: right, top: sideTop };
  const leftSide = target.left - PANEL_GAP - panel.width;
  if (leftSide >= EDGE) return { left: leftSide, top: sideTop };

  return { left, top: Math.max(EDGE, viewport.height - panel.height - EDGE) };
}
